import { Box, Flex, Image } from '@chakra-ui/react';
import { AiResponseEncodedUiComponents } from '@worknet/models';
import { useCalendlyEventListener, InlineWidget } from 'react-calendly';
import LoomPlayer from 'react-loom-player';
import ReactPlayer from 'react-player';

import useChatStore from '~/shared/utils/useChatStore';

import { HubSpotScheduling } from './HubSpotScheduling';

type WnComponentProps<T extends AiResponseEncodedUiComponents['type']> = {
  components: Extract<AiResponseEncodedUiComponents, { type: T }>;
  interactionsDisabled: boolean;
};

function WnMarkdownScheduling({ components }: WnComponentProps<'wn-scheduling'>) {
  const { appValue, sendMessage } = useChatStore(({ app, sendMessage }) => ({
    appValue: app.value!,
    sendMessage,
  }));

  useCalendlyEventListener({
    onEventScheduled: (e) => {
      sendMessage({
        actionId: appValue.amaAction.id,
        content: `A calendly event has been successfully scheduled.\n${JSON.stringify(
          e.data.payload
        )}`,
        display: `Event has been scheduled successfully!`,
      });
    },
  });

  if (components.platform === 'hubspot') {
    return (
      <HubSpotScheduling
        schedulingUrl={components.schedulingUrl}
        actionId={appValue.amaAction.id}
      />
    );
  }

  if (components.platform === 'calendly') {
    return <InlineWidget url={components.schedulingUrl} />;
  }

  return null;
}

function WnMarkdownButtonsGroup({
  components,
  interactionsDisabled,
}: WnComponentProps<'wn-buttons-group'>) {
  const { appValue, sendMessage } = useChatStore(({ app, sendMessage }) => ({
    appValue: app.value!,
    sendMessage,
  }));

  return (
    <Flex flexWrap={'wrap'} gap={2} minW={0}>
      {components.buttons.map((option) => (
        <Box
          as={'button'}
          key={option.prompt}
          py={1.5}
          px={3.5}
          bg={'white'}
          color={'black'}
          lineHeight={1.5}
          fontFamily={'Inter'}
          borderColor={'main'}
          borderWidth={1}
          borderRadius={100}
          disabled={interactionsDisabled}
          maxW={'100%'}
          whiteSpace={'nowrap'}
          overflow={'hidden'}
          textOverflow={'ellipsis'}
          _hover={
            !interactionsDisabled
              ? {
                  bg: '#EDF1FF',
                }
              : {}
          }
          _disabled={{
            bg: '#D9D9D9',
            color: '#828282',
            cursor: 'not-allowed',
          }}
          onClick={() => {
            sendMessage({
              actionId: appValue.amaAction.id,
              content: option.prompt,
              display: option.label,
            });
          }}
          title={option.label}
        >
          {option.label}
        </Box>
      ))}
    </Flex>
  );
}

function WnMarkdownVideoPlayer({
  components,
  interactionsDisabled,
}: WnComponentProps<'wn-video-player'>) {
  const autoplay = !interactionsDisabled && components.autoPlay;
  return components.videoPlatform.platform === 'loom' ? (
    <LoomPlayer
      src={components.videoUrl}
      autoplay={autoplay}
      hideEmbedTopBar
      hideTitle
      hideOwner
      hideShare
    />
  ) : (
    <ReactPlayer
      src={components.videoUrl}
      autoPlay={autoplay}
      controls={true}
      style={{ width: '100%', height: 'auto', aspectRatio: '16/9' }}
    />
  );
}

function WnMarkdownImageViewer({ components }: WnComponentProps<'wn-image-viewer'>) {
  return <Image src={components.imageUrl} width={'100%'} />;
}

export default function WnMarkdownUiComponents({
  components,
  interactionsDisabled,
}: {
  components: AiResponseEncodedUiComponents[];
  interactionsDisabled: boolean;
}) {
  return (
    <Flex direction={'column'} gap={4} my={4}>
      {components.map((components, index) => {
        switch (components.type) {
          case 'wn-scheduling':
            return (
              <WnMarkdownScheduling
                key={index}
                components={components}
                interactionsDisabled={interactionsDisabled}
              />
            );
          case 'wn-buttons-group':
            return (
              <WnMarkdownButtonsGroup
                key={index}
                components={components}
                interactionsDisabled={interactionsDisabled}
              />
            );
          case 'wn-video-player':
            return (
              <WnMarkdownVideoPlayer
                key={index}
                components={components}
                interactionsDisabled={interactionsDisabled}
              />
            );
          case 'wn-image-viewer':
            return (
              <WnMarkdownImageViewer
                key={index}
                components={components}
                interactionsDisabled={interactionsDisabled}
              />
            );
          default:
            return (
              <span key={index}>
                {`Unknown wn ui component type: ${JSON.stringify(components satisfies never)}`}
              </span>
            );
        }
      })}
    </Flex>
  );
}
